import { LEGACY_DASHBOARD_GUIDE_KEY } from "./shared/types";
import { APP_RELEASE_ID } from "./config/appRelease";
import {
  describeLegacyOnboardingPending,
  describeShouldEvaluateEducation,
  logBootGateDecision,
  logBootGateSkipped,
  logBootGateStarted,
  logEducationSkipped,
} from "./shared/pepBootDebug";
import {
  isDashboardBlockedByPlan,
  planReleaseExperience,
} from "./experience/ExperiencePlanner";
const AUTH_SCREENS = new Set([
  "onboarding",
  "email_confirmation",
  "update_password",
  "payroll_print_isolation",
]);

/**
 * Determines whether Product Education should evaluate on this boot.
 * Priority: auth flows → (optional) release education when enabled → legacy first-time guide → normal app.
 */
export function shouldEvaluateEducation({
  screen,
  authLoading,
  hasUser,
  userId,
  autoEvaluatedRef,
}) {
  const skipReason = describeShouldEvaluateEducation({
    screen,
    authLoading,
    hasUser,
    userId,
    autoEvaluatedRef,
  });

  if (skipReason) {
    logBootGateSkipped(skipReason);
    return false;
  }

  logBootGateStarted("shouldEvaluateEducation");
  return true;
}
export function shouldDeferToLegacyFirstTimeGuide(settings) {
  if (!settings?.autoFirstTimeGuideEnabled) {
    return false;
  }

  if (typeof window === "undefined") {
    return false;
  }

  return !window.localStorage.getItem(LEGACY_DASHBOARD_GUIDE_KEY);
}

/**
 * Maps an ExperiencePlan to the presentation layer should render.
 */
export function resolveEducationPresentation(plan, settings = null) {
  if (!plan) {
    logEducationSkipped("no experience plan was produced");
    return { type: "none" };
  }

  if (plan.showRequiredTour) {
    const legacyPending = describeLegacyOnboardingPending(settings);
    logBootGateDecision({
      legacyOnboardingPending: legacyPending,
      releaseEducation: true,
      normalStartup: false,
      presentationType: "required_tour",
      planReason: plan.reason,
    });
    return { type: "required_tour", plan };
  }

  if (plan.showBrief) {
    const legacyPending = describeLegacyOnboardingPending(settings);
    logBootGateDecision({
      legacyOnboardingPending: legacyPending,
      releaseEducation: true,
      normalStartup: false,
      presentationType: "brief",
      planReason: plan.reason,
    });
    return { type: "brief", plan };
  }

  if (plan.showWhatsNewChip) {
    const legacyPending = describeLegacyOnboardingPending(settings);
    logBootGateDecision({
      legacyOnboardingPending: legacyPending,
      releaseEducation: false,
      normalStartup: false,
      presentationType: "whats_new_chip",
      planReason: plan.reason,
    });
    return { type: "whats_new_chip", plan };
  }

  const legacyPending = describeLegacyOnboardingPending(settings);
  const legacyWins =
    legacyPending &&
    plan.reason === "first_time_guide_priority";

  logBootGateDecision({
    legacyOnboardingPending: legacyPending,
    releaseEducation: false,
    normalStartup: !legacyWins,
    presentationType: "none",
    planReason: plan.reason,
  });

  if (legacyWins) {
    logEducationSkipped(
      "legacy first-time dashboard guide has priority over release education this session",
    );
  } else {
    logEducationSkipped(`plan reason is ${plan.reason}`);
  }

  return { type: "none", plan };
}

/**
 * Returns true when the legacy Driver.js dashboard guide must not auto-launch.
 * Manual replay via the Guide menu is unaffected.
 *
 * Note: KPIDashboardScreen no longer auto-starts the legacy tour; this gate
 * remains for any residual callers / product-education coordination.
 */
export function shouldSuppressLegacyAutoGuide({
  educationBootComplete = false,
  activeView = "none",
  showLauncherMenu = false,
  activePlan = null,
  educationState = null,
  releaseId = APP_RELEASE_ID,
} = {}) {
  if (!educationBootComplete) {
    return true;
  }

  if (activeView !== "none" || showLauncherMenu) {
    return true;
  }

  if (isDashboardBlockedByPlan(activePlan)) {
    return true;
  }

  if (activePlan?.showBrief || activePlan?.showRequiredTour || activePlan?.requiredTour) {
    return true;
  }

  const releaseRecord = educationState?.releases?.[releaseId];
  if (releaseRecord?.status === "in_progress") {
    return true;
  }

  if (
    activePlan?.educationPolicy === "mandatory" &&
    releaseRecord?.status !== "completed" &&
    releaseRecord?.status !== "skipped"
  ) {
    return true;
  }

  return false;
}

export { isDashboardBlockedByPlan, planReleaseExperience };
